// Centralized audio utility

// Creates an Audio element that plays through the speaker on iOS even when
// the mic is active (PlayAndRecord audio session). new Audio() does not work
// for this — a DOM element with playsinline is required.
const createAudio = (src: string): HTMLAudioElement => {
  const audio = document.createElement('audio');
  audio.setAttribute('playsinline', '');
  audio.setAttribute('webkit-playsinline', '');
  audio.src = src;
  return audio;
};

export const playSuccessSound = async () => {
  try {
    const audio = createAudio('/success.mp3');
    audio.volume = 0.5;

    await audio.play();
    return new Promise((resolve) => {
      audio.onended = () => resolve(true);
    });
  } catch (error) {
    console.warn('Audio playback failed, using fallback sound');
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
      gainNode.gain.setValueAtTime(0.5, audioContext.currentTime);

      oscillator.start();
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

      return new Promise((resolve) => {
        setTimeout(() => {
          oscillator.stop();
          resolve(true);
        }, 500);
      });
    } catch (fallbackError) {
      console.error('Fallback audio also failed:', fallbackError);
      return Promise.resolve(false);
    }
  }
};

export const playLessonCompleteSound = async () => {
  try {
    const audio = createAudio('/tada.mp3');
    audio.volume = 0.8;
    await audio.play();
    return new Promise((resolve) => {
      audio.onended = () => resolve(true);
    });
  } catch (error) {
    console.warn('Could not play tada.mp3, falling back to Web Audio API');
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const gainNode = audioContext.createGain();
      gainNode.connect(audioContext.destination);

      const notes = [
        { freq: 523.25, duration: 0.1 },
        { freq: 659.25, duration: 0.1 },
        { freq: 783.99, duration: 0.1 },
        { freq: 1046.50, duration: 0.2 },
        { freq: 1318.51, duration: 0.1 },
        { freq: 1567.98, duration: 0.3 },
      ];

      let startTime = audioContext.currentTime;

      notes.forEach(note => {
        const oscillator = audioContext.createOscillator();
        oscillator.connect(gainNode);
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(note.freq, startTime);

        gainNode.gain.setValueAtTime(0.4, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + note.duration);

        oscillator.start(startTime);
        oscillator.stop(startTime + note.duration);

        startTime += note.duration;
      });

      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(true);
        }, 1000);
      });
    } catch (fallbackError) {
      console.error('Fallback audio also failed:', fallbackError);
      return Promise.resolve(false);
    }
  }
};

export const playWrongSound = async () => {
  try {
    try {
      const audio = createAudio('/error.mp3');
      audio.volume = 0.5;
      await audio.play();
      return new Promise(resolve => {
        audio.onended = () => resolve(true);
      });
    } catch (error) {
      console.warn('Could not play error.mp3, falling back to Web Audio API');
    }

    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(220, audioContext.currentTime);
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);

    oscillator.start();
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);

    return new Promise((resolve) => {
      setTimeout(() => {
        oscillator.stop();
        resolve(true);
      }, 200);
    });
  } catch (error) {
    console.error('Error playing wrong sound:', error);
    return Promise.resolve(false);
  }
};

export const playDoneSound = async () => {
  try {
    const audio = createAudio('/done.mp3');
    audio.volume = 0.1;

    await audio.play();
    return new Promise((resolve) => {
      audio.onended = () => resolve(true);
    });
  } catch (error) {
    console.warn('Audio playback failed, using fallback sound');
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(660, audioContext.currentTime);
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);

      oscillator.start();
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.8);

      return new Promise((resolve) => {
        setTimeout(() => {
          oscillator.stop();
          resolve(true);
        }, 800);
      });
    } catch (fallbackError) {
      console.error('Fallback audio also failed:', fallbackError);
      return Promise.resolve(false);
    }
  }
};
